// client/src/components/pages/LexiStar/RouteTruckDriver/RouteTruckDriver.tsx
//
// LexiStar · Analyse → "Routes, trucks & drivers" (ST-00254 fuel used vs the diesel allowance,
// ST-00255 tonnes moved, ST-00256 speeding; LS-03 FR-RPT-001 / FR-RPT-002 / FR-RPT-006). Route
// /lexistar/routes-trucks-drivers, page permission lexistar.kpis.view; the driver dimension
// also needs lexistar.drivers.view (LS-07: individual behaviour is permission-gated).
//
// One area, three measures, one dimension switch: pick a measure (fuel, tonnes, speeding) and
// what a bar stands for (a route, a truck, a driver), choose the period, and read the ranked
// bars, the trend and the table. A click on a bar or a row selects it: the trend becomes that
// row's, and for speeding the episodes behind it are listed; the row's name opens the trips
// behind it. "How is this counted?" shows the governed definitions; Export CSV saves the table.
// Fuel and tonnes read the governed metrics (GET /kpis/breakdown with series, GET /kpis for the
// operation's trend); speeding reads GET /speeding (+ /speeding/events). The page formats; it
// computes no figure (m4-contracts §1). Measure, dimension, period, ranking and selection live
// in the URL.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaFileExport, FaSortAmountDown, FaTimes } from 'react-icons/fa';
import { getKpiBreakdown, getKpis, getMetrics, getSpeeding } from '../../../../services/lexistarAnalytics';
import type { BreakdownResponse, KpisResponse, MetricsCatalogueResponse, SpeedingResponse } from '../../../../types/lexistar-analytics';
import { NoPermissionPage } from '../StatusBoard/atoms';
import { PageHeader, SegmentedControl, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { PeriodPicker, ProvenanceLegend, ProvenanceMark, SkeletonBlock, StateChip, cardClasses } from '../Dashboard/analyticsAtoms';
import { type MetricDef, normaliseBreakdown, normaliseCatalogue, type Breakdown } from '../Dashboard/kpiModel';
import { grainFor, periodLabel, readPeriod, type Grain } from '../Dashboard/periodLogic';
import { usePhoneLayout } from '../../../common/phoneLayout';
import EpisodesPanel from './EpisodesPanel';
import RankedBars from './RankedBars';
import TrendChart from './TrendChart';
import {
  DIMS,
  DRIVERS_VIEW,
  MEASURES,
  type Dim,
  type MeasureId,
  type RtdColumn,
  type RtdRow,
  type TrendPoint,
  buildCsv,
  chartRows,
  csvFileName,
  definitionOf,
  formatCell,
  measureOf,
  rankRows,
  readDim,
  readMeasure,
  readRank,
  rowsFromBreakdowns,
  rowsFromSpeeding,
  trendFromKpis,
  trendFromRow,
  trendFromSpeedingSeries,
} from './rtdLogic';

const TOP_BARS = 12;
const PAGE = 25;

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
const errorText = (err: ErrorLike, fallback: string): string =>
  err?.response?.status === 403
    ? 'You do not have permission to read these figures.'
    : err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

/** Save text as a file (the StatusBoard's CSV export does the same). */
function saveCsv(text: string, name: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function RouteTruckDriver() {
  const { hasPermission } = useAuth();
  const phone = usePhoneLayout();
  const canView = hasPermission('lexistar.kpis.view');
  const canSeeDrivers = hasPermission(DRIVERS_VIEW);
  const [params, setParams] = useSearchParams();
  const measureId: MeasureId = readMeasure(params);
  const measure = measureOf(measureId);
  const wantedDim = readDim(params);
  const dim: Dim = wantedDim === 'driver' && !canSeeDrivers ? 'route' : wantedDim;
  const dimSpec = DIMS.find((d) => d.id === dim) ?? DIMS[0]!;
  const period = readPeriod(params);
  const periodKey = `${period.from}|${period.to}`;
  const rank: RtdColumn = readRank(params, measure);
  const selectedId = params.get('row');

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  // ── The catalogue (definitions; which governed columns the caller may read) ──
  const [catalogue, setCatalogue] = useState<Map<string, MetricDef> | null>(null);
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    getMetrics()
      .then((res: MetricsCatalogueResponse) => alive && setCatalogue(normaliseCatalogue(res)))
      .catch(() => alive && setCatalogue(new Map()));
    return () => {
      alive = false;
    };
  }, [canView]);

  const columns: RtdColumn[] = useMemo(
    () => (measureId === 'speeding' || !catalogue ? measure.columns : measure.columns.filter((c) => catalogue.has(c.key))),
    [measure, measureId, catalogue]
  );
  const rankColumn: RtdColumn = columns.find((c) => c.key === rank.key) ?? columns[0] ?? rank;
  const compareColumn: RtdColumn | null =
    rankColumn.key === measure.primary && measure.compare ? columns.find((c) => c.key === measure.compare) ?? null : null;

  // ── The figures ─────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<RtdRow[]>([]);
  const [operationTrend, setOperationTrend] = useState<TrendPoint[]>([]);
  const [grain, setGrain] = useState<Grain>(grainFor(period));
  const [basis, setBasis] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [limit, setLimit] = useState(PAGE);
  const governedKeys = measureId === 'speeding' ? '' : columns.map((c) => c.key).join(',');
  useEffect(() => {
    if (!canView) return undefined;
    if (measureId !== 'speeding' && !catalogue) return undefined;
    let alive = true;
    setLoading(true);
    setErrors([]);
    setLimit(PAGE);
    const done = (next: RtdRow[], trend: TrendPoint[], g: Grain, errs: string[], b: string | null) => {
      if (!alive) return;
      setRows(next);
      setOperationTrend(trend);
      setGrain(g);
      setErrors(errs);
      setBasis(b);
      setLoading(false);
    };
    if (measureId === 'speeding') {
      getSpeeding({ by: dim, from: period.from, to: period.to })
        .then((res: SpeedingResponse) => done(rowsFromSpeeding(res), trendFromSpeedingSeries(res.series), res.grain, [], res.basis))
        .catch((err: ErrorLike) => done([], [], grainFor(period), [errorText(err, 'Speeding could not be loaded.')], null));
      return () => {
        alive = false;
      };
    }
    const keys = governedKeys ? governedKeys.split(',') : [];
    if (!keys.length) {
      done([], [], grainFor(period), [], null);
      return () => {
        alive = false;
      };
    }
    const g = grainFor(period);
    // The operation's trend carries every column, so a change of ranking needs no new request.
    const trendKeys = keys;
    Promise.all([
      ...keys.map((key) =>
        getKpiBreakdown({ metric: key, by: dim, from: period.from, to: period.to, series: true })
          .then((res: BreakdownResponse) => ({ key, b: normaliseBreakdown(res) as Breakdown | undefined, error: null as string | null }))
          .catch((err: ErrorLike) => ({
            key,
            b: undefined,
            error: `${measure.columns.find((c) => c.key === key)?.label ?? key}: ${errorText(err, 'could not be loaded')}`,
          }))
      ),
      getKpis({ grain: g, from: period.from, to: period.to, metrics: trendKeys })
        .then((res: KpisResponse) => ({ trend: trendFromKpis(res, trendKeys), error: null as string | null }))
        .catch((err: ErrorLike) => ({ trend: [] as TrendPoint[], error: `Trend: ${errorText(err, 'could not be loaded')}` })),
    ]).then((results) => {
      const byKey: Record<string, Breakdown | undefined> = {};
      const errs: string[] = [];
      let trend: TrendPoint[] = [];
      results.forEach((r) => {
        if ('key' in r) {
          byKey[r.key] = r.b;
          if (r.error) errs.push(r.error);
        } else {
          trend = r.trend;
          if (r.error) errs.push(r.error);
        }
      });
      done(rowsFromBreakdowns(byKey, keys), trend, g, errs, null);
    });
    return () => {
      alive = false;
    };
    // periodKey / governedKeys carry the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, measureId, dim, periodKey, governedKeys, catalogue === null]);

  const ranked = useMemo(() => rankRows(rows, rankColumn), [rows, rankColumn]);
  const bars = useMemo(() => chartRows(ranked, rankColumn, TOP_BARS), [ranked, rankColumn]);
  const shown = ranked.slice(0, limit);
  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;
  const trendKeys = [rankColumn.key, ...(compareColumn ? [compareColumn.key] : [])];
  const trend: TrendPoint[] = selected ? trendFromRow(selected, trendKeys) : operationTrend;
  const provisionalRows = rows.filter((r) => r.state === 'provisional').length;

  const select = (id: string | null) => update({ row: id && id !== selectedId ? id : null });
  const exportCsv = () => saveCsv(buildCsv(ranked, columns, dimSpec.noun), csvFileName(measureId, dim, period.from, period.to));

  // ── Render ──────────────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      icon={FaSortAmountDown}
      title="Routes, trucks & drivers"
      subtitle="Fuel against the allowance, tonnes moved and speeding — ranked by route, truck or driver for the period, with the trips and episodes behind each bar."
      toolbar={canView ? <PeriodPicker period={period} phone={phone} onChange={(p) => update({ from: p.from, to: p.to, row: null })} /> : null}
      inline={false}
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view these figures." />;

  const noun = dimSpec.noun;
  const dimOptions = DIMS.filter((d) => d.id !== 'driver' || canSeeDrivers).map((d) => ({ value: d.id, label: d.label }));

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl<MeasureId>
            ariaLabel="Measure"
            value={measureId}
            onChange={(m) => update({ measure: m, rank: null, row: null })}
            options={MEASURES.map((m) => ({ value: m.id, label: m.label, shortLabel: m.shortLabel }))}
          />
          <SegmentedControl<Dim>
            ariaLabel="One bar is one"
            value={dim}
            onChange={(d) => update({ by: d, row: null })}
            options={dimOptions}
          />
          <p className="ml-auto text-xs text-slate-600 tabular-nums" role="status" aria-live="polite">
            {periodLabel(period)}
            {!loading && ` · ${rows.length} ${noun.toLowerCase()}${rows.length === 1 ? '' : 's'}`}
            {provisionalRows > 0 && ` · ${provisionalRows} with provisional figures`}
          </p>
        </div>

        {wantedDim === 'driver' && !canSeeDrivers && (
          <p role="alert" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Driver figures need permission to view drivers. Showing routes instead.
          </p>
        )}
        {dim === 'driver' && (
          <p className="text-xs text-slate-600 bg-surface border border-slate-200 rounded-lg px-3 py-2">
            Driver figures are for coaching: they describe the trips a driver ran, on the routes and trucks they were given. Look at
            trends over weeks, not at single trips.
          </p>
        )}

        <section className={`${cardClasses} px-4 py-3`} aria-labelledby="rtd-definition">
          <p id="rtd-definition" className="text-sm text-slate-700">
            {measure.definition}
          </p>
          <details className="group mt-1 text-[11px] text-slate-500">
            <summary className="touch-target cursor-pointer select-none list-none inline-flex items-center gap-1 hover:text-slate-800 [&::-webkit-details-marker]:hidden">
              <span aria-hidden="true" className="transition-transform group-open:rotate-90">
                ›
              </span>
              How is this counted?
            </summary>
            <dl className="mt-1.5 rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1.5 text-slate-700">
              {measureId === 'speeding' ? (
                <div>
                  <dt className="font-medium">Speeding</dt>
                  <dd>{basis || measure.definition}</dd>
                  <dd className="mt-1 text-slate-500">
                    Units: episodes (count), time over the limit (seconds, shown as minutes and hours), worst excess (km/h over the
                    limit), episodes per 100 km of the distance shown.
                  </dd>
                </div>
              ) : (
                columns.map((c) => {
                  const def = definitionOf(catalogue, c.key);
                  return (
                    <div key={c.key}>
                      <dt className="font-medium">
                        {c.label}
                        {def ? ` · ${def.unit}` : ''}
                      </dt>
                      {def && <dd>{def.description}</dd>}
                      {def && <dd className="font-mono text-[11px]">{def.formula}</dd>}
                    </div>
                  );
                })
              )}
            </dl>
          </details>
        </section>

        {errors.length > 0 && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {errors.join('; ')}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-5">
          <section className={`${cardClasses} p-4 lg:col-span-3 min-w-0`} aria-labelledby="rtd-ranked-title">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h2 id="rtd-ranked-title" className="text-sm font-semibold text-slate-900">
                {noun}s by {rankColumn.label.toLowerCase()}
              </h2>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                Rank by
                <span className="w-44">
                  <ThemedSelect
                    aria-label="Rank by"
                    value={rankColumn.key}
                    onChange={(e) => e.target.value && update({ rank: e.target.value })}
                    className="text-sm"
                  >
                    {columns
                      .filter((c) => c.rank)
                      .map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                  </ThemedSelect>
                </span>
              </label>
            </div>
            {loading ? (
              <div className="space-y-2.5" aria-busy="true">
                {Array.from({ length: 6 }, (_, i) => (
                  <SkeletonBlock key={i} className="h-6" />
                ))}
              </div>
            ) : !bars.length ? (
              <p className="text-sm text-slate-600 py-8 text-center">No {noun.toLowerCase()} recorded {rankColumn.label.toLowerCase()} in this period.</p>
            ) : (
              <RankedBars rows={bars} column={rankColumn} compare={compareColumn} selected={selectedId} onSelect={select} labelWidth={phone ? 112 : 200} />
            )}
            {!loading && ranked.length > bars.length && (
              <p className="mt-2 text-xs text-slate-500">
                Top {bars.length} of {ranked.length} — the table below lists them all.
              </p>
            )}
          </section>

          <section className={`${cardClasses} p-4 lg:col-span-2 min-w-0`} aria-labelledby="rtd-trend-title">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h2 id="rtd-trend-title" className="text-sm font-semibold text-slate-900 truncate">
                Trend · {selected ? selected.label : 'whole operation'}
              </h2>
              {selected && (
                <button
                  type="button"
                  onClick={() => select(null)}
                  className="touch-target inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                >
                  <FaTimes size={10} aria-hidden="true" /> Whole operation
                </button>
              )}
            </div>
            {loading ? (
              <SkeletonBlock className="h-48" />
            ) : (
              <TrendChart points={trend} column={rankColumn} compare={compareColumn} grain={grain} subject={selected ? selected.label : 'the whole operation'} />
            )}
            {!loading && !selected && <p className="mt-2 text-xs text-slate-500">Click a bar or a row to see that {noun.toLowerCase()}’s trend.</p>}
          </section>
        </div>

        <section className={`${cardClasses} overflow-hidden`} aria-label={`${noun} table`}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3 pb-2">
            <h2 className="text-sm font-semibold text-slate-900">Every {noun.toLowerCase()}</h2>
            <button
              type="button"
              onClick={exportCsv}
              disabled={loading || !ranked.length}
              className="touch-target inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-surface px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <FaFileExport size={11} aria-hidden="true" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th scope="col" className="sticky left-0 z-[1] bg-slate-50 px-3 py-2.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    {noun}
                  </th>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      title={c.label}
                      aria-sort={c.key === rankColumn.key ? 'descending' : 'none'}
                      className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700 uppercase tracking-wide whitespace-nowrap"
                    >
                      {c.rank ? (
                        <button type="button" onClick={() => update({ rank: c.key })} className="uppercase tracking-wide hover:underline">
                          {c.short}
                          {c.key === rankColumn.key ? ' ↓' : ''}
                        </button>
                      ) : (
                        c.short
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }, (_, i) => (
                    <tr key={i}>
                      <td colSpan={columns.length + 1} className="px-3 py-2">
                        <SkeletonBlock className="h-6" />
                      </td>
                    </tr>
                  ))
                ) : !ranked.length ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-3 py-10 text-center text-sm text-slate-600">
                      No {noun.toLowerCase()}s recorded in this period.
                    </td>
                  </tr>
                ) : (
                  shown.map((r) => {
                    const isSelected = r.id === selectedId;
                    return (
                      <tr
                        key={r.id}
                        aria-selected={isSelected}
                        onClick={() => select(r.id)}
                        className={`cursor-pointer ${isSelected ? 'bg-primary-50' : 'hover:bg-slate-50'}`}
                      >
                        <th scope="row" className={`sticky left-0 z-[1] px-3 py-2 text-left font-normal max-w-[16rem] ${isSelected ? 'bg-primary-50' : 'bg-surface'}`}>
                          {r.drill ? (
                            <Link
                              to={r.drill}
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium text-slate-900 hover:underline truncate block"
                              title={`Open the trips behind ${r.label}`}
                            >
                              {r.label}
                            </Link>
                          ) : (
                            <span className="font-medium text-slate-900 truncate block">{r.label}</span>
                          )}
                          {r.state === 'provisional' && <StateChip state="provisional" className="mt-0.5" />}
                          {r.kmBasis === 'gps' && <span className="block text-[11px] text-slate-500">km from GPS</span>}
                        </th>
                        {columns.map((c) => {
                          const v = r.values[c.key];
                          return (
                            <td key={c.key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-800">
                              {v === null || v === undefined ? (
                                <span className="text-slate-400">—</span>
                              ) : (
                                <>
                                  {formatCell(c, v)}
                                  {r.provenance[c.key] && <ProvenanceMark provenance={r.provenance[c.key]} className="ml-1" />}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && ranked.length > shown.length && (
            <div className="border-t border-slate-100 px-3 py-2.5 text-center">
              <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="text-sm font-medium text-primary-700 hover:underline">
                Show {Math.min(PAGE, ranked.length - shown.length)} more of {ranked.length - shown.length}
              </button>
            </div>
          )}
        </section>

        {measureId === 'speeding' && !loading && !errors.length && (
          <EpisodesPanel by={dim} scopeId={selected ? selected.id : null} label={selected ? selected.label : null} from={period.from} to={period.to} />
        )}

        <p className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          {measureId !== 'speeding' && <ProvenanceLegend />}
          <span>Click a {noun.toLowerCase()}’s name to open the trips behind it.</span>
        </p>
      </div>
    </div>
  );
}
